import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Cookie } from "lucide-react";

const KEY = "aria:consent:v1";

export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [hasBottomNav, setHasBottomNav] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // App Store builds don't need a cookie consent banner — web only.
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) return;
      } catch {
        /* no native bridge — treat as web */
      }
      try {
        if (!cancelled && !localStorage.getItem(KEY)) setOpen(true);
      } catch {
        // ignore (SSR / privacy mode)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const check = () => setHasBottomNav(!!document.querySelector("[data-bottom-nav]"));
    check();
    const t = window.setTimeout(check, 300);
    return () => window.clearTimeout(t);
  }, [open]);

  const accept = () => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ accepted: true, at: new Date().toISOString() }),
      );
    } catch {
      // ignore
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookies and disclaimer"
      className="fixed inset-x-0 bottom-0 z-[70] px-3 pt-3 pb-[calc(env(safe-area-inset-bottom)+var(--cb-pb))] md:px-6 md:pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
      style={{ ["--cb-pb" as string]: hasBottomNav ? "6rem" : "0.75rem" } as any}
    >
      <div className="glass mx-auto w-full max-w-[640px] rounded-3xl ring-1 ring-primary/10 shadow-xl p-4 animate-in-fade md:max-w-[880px] md:p-4 lg:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
          <div className="flex min-w-0 items-start gap-3 md:items-center">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-primary/15 text-primary">
              <Cookie className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Cookies &amp; a quick heads-up</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground md:text-[12.5px]">
                Pyng uses essential cookies to keep you signed in and remember your
                preferences. Pyng is an AI assistant — drafts and suggestions are for
                your review, never auto-sent.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 md:ml-auto">
            <Link
              to="/privacy"
              className="text-xs text-muted-foreground underline underline-offset-2 transition hover:text-foreground"
            >
              Privacy &amp; terms
            </Link>
            <button
              onClick={accept}
              className="inline-flex h-11 flex-1 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground transition active:scale-[0.98] md:h-10 md:flex-none"
            >
              Accept &amp; continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
